// Global Instructions Rule Applied!

import React, { useState, useCallback, useMemo, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { Popconfirm, Dropdown, Button } from 'antd'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisV, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FormTable } from '../../../../core/components/FormTable/index.tsx'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import {
  FieldTypeEnum,
  CompanyFieldConfig,
  SelectFieldConfig,
  TextFieldConfig,
  CheckboxFieldConfig
} from '../../../../core/components/InputManager/types.ts'
import { CompanyTeamEntry, CompanyTeamEntryFields } from '../../../../state_manager/data_models/company_team_entry.ts'
import { RelationshipTypesModelFields } from '../../../../state_manager/data_models/relationship_types_model.ts'
import { RelationshipCategoriesModelFields } from '../../../../state_manager/data_models/relationship_categories_model.ts'
import { BUTTON_CLASSES, TEXT_CLASSES, UI } from '../../../../core/components/FormTable/constants.ts'
import { getColumnConfig } from '../../../../core/components/CompanyTeamTable/utils/fieldConfigs.tsx'
import type { RelationshipCategoryOption } from '../../../../core/components/CompanyTeamTable/utils/utils.ts'
import type { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'
import type { RootState } from '../../../../state_manager/store.ts'
import { createCustomValidationRule, validateAndShowErrors } from '../../../../core/components/FormTable/validation.ts'
import { RELATIONSHIP_TYPE_EMPLOYEE, RELATIONSHIP_TYPE_ADVISOR } from '../../../../state_manager/globals.ts'
import { toast } from 'react-toastify'
import { TOAST_CONFIG } from '../../../../core/components/FormTable/constants.ts'

interface ContactRolesTableProps {
  mode: 'current' | 'prior'
  roles: CompanyTeamEntry[]
  onChange: (roles: CompanyTeamEntry[]) => void
  contactId: string | null | undefined
  getCompanyLabelById?: (companyId: string | null | undefined) => string
  getCategoryLabelById?: (categoryId: string | null | undefined) => string
  onMoveToPrior?: (entry: CompanyTeamEntry) => Promise<void>
  onMoveToCurrent?: (entry: CompanyTeamEntry) => Promise<void>
  onSetPrimary?: (entry: CompanyTeamEntry) => Promise<void>
  onDelete?: (entry: CompanyTeamEntry) => Promise<void>
  onSave?: (savedRole: any, allRoles: any[]) => Promise<void>
  readOnly?: boolean
}

// A role as the table edits it: the CompanyTeamEntry flattened to its editable fields. A type alias, not an
// interface: FormTable's record constraint carries an index signature, and only aliases get the implicit one.
type EditableRole = {
  key: string
  companyId: string
  companyName: string
  categoryId: string
  roleTypeId: string
  title: string
  isPrimary: boolean
  /** The entry the row was built from; null for a row added in this session. */
  originalRole: CompanyTeamEntry | null
}

/** A relationship category as the Category select lists it: the cfg row's id and type, plus value/label. */
type CategoryOption = SelectListEntry & Pick<RelationshipCategoryOption, 'id' | 'relationship_type_id'>

// Helper function to check if a role would be a duplicate
const isDuplicateRole = (newRole: any, existingRoles: any[], excludeKey: string | null = null) => {
  const newRoleTypeId = (newRole.roleTypeId || '').trim()
  const newCompanyId = (newRole.companyId || '').trim()
  const newCategoryId = (newRole.categoryId || '').trim()

  // Don't check for duplicates if required fields are empty
  if (!newCompanyId || !newCategoryId) {
    return false
  }

  return existingRoles.some((role) => {
    // Skip the role being edited
    if (excludeKey && role.key === excludeKey) {
      return false
    }

    const existingRoleTypeId = (role.roleTypeId || '').trim()
    const existingCompanyId = (role.companyId || '').trim()
    const existingCategoryId = (role.categoryId || '').trim()

    // Check for duplicates by: same roleTypeId, companyId, and categoryId
    // Note: roleTypeId can be empty/null, so we need to handle that case
    const roleTypeMatch = (!newRoleTypeId && !existingRoleTypeId) || newRoleTypeId === existingRoleTypeId
    const companyMatch = newCompanyId === existingCompanyId
    const categoryMatch = newCategoryId === existingCategoryId

    return roleTypeMatch && companyMatch && categoryMatch
  })
}

/**
 * Contact Roles Table Component
 * Displays current or prior roles for a contact using CompanyTeamEntry data model
 * Supports inline editing and adding new roles
 */
export const ContactRolesTable = forwardRef<{ hasPendingRoles: () => boolean }, ContactRolesTableProps>(
  (
    {
      mode,
      roles = [],
      onChange,
      contactId,
      getCompanyLabelById,
      getCategoryLabelById,
      onMoveToPrior,
      onMoveToCurrent,
      onSetPrimary,
      onDelete,
      onSave,
      readOnly = false
    },
    ref
  ) => {
    const relationshipTypesRaw = useSelector((state: any) => state.globalConfigs?.relationship_types || [])
    const relationshipTypes = useMemo(() => relationshipTypesRaw || [], [relationshipTypesRaw])
    const relationshipCategoriesRaw = useSelector(
      (state: RootState) => state.globalConfigs?.relationship_categories || []
    )
    const relationshipCategories = useMemo(() => relationshipCategoriesRaw || [], [relationshipCategoriesRaw])

    // State management for inline editing
    const [editingRowKey, setEditingRowKey] = useState<string | null>(null)
    const [savingRowKey, setSavingRowKey] = useState<string | null>(null)
    const [isAddingNew, setIsAddingNew] = useState(false)
    const [validationErrors, setValidationErrors] = useState<Record<string, Record<string, string>>>({})
    const [firstErrorFieldKey, setFirstErrorFieldKey] = useState<{ rowKey: string; fieldKey: string } | null>(null)
    const [originalRoleData, setOriginalRoleData] = useState<any>(null)

    // Transform roles to internal editable format
    const [editableRoles, setEditableRoles] = useState<EditableRole[]>(() => {
      if (!roles || !Array.isArray(roles)) return []
      return roles.map((role) => {
        const roleId = role[CompanyTeamEntryFields.contact_accounts__id] || `role-${crypto.randomUUID()}`
        const companyId = role[CompanyTeamEntryFields.contact_accounts__account_id] || ''
        const companyName = role[CompanyTeamEntryFields.prog__account__name] || ''
        const categoryId = role[CompanyTeamEntryFields.contact_accounts__relationship_category_id] || ''
        const roleTypeId = role[CompanyTeamEntryFields.relationship_types__id] || ''
        const title = role[CompanyTeamEntryFields.contact_accounts__title] || ''

        const isPrimary = role[CompanyTeamEntryFields.contact_accounts__is_primary] === true

        return {
          key: roleId,
          companyId,
          companyName,
          categoryId,
          roleTypeId,
          title,
          isPrimary,
          // Store original role data for reference
          originalRole: role
        }
      })
    })

    // Update editableRoles when roles prop changes (but not when editing)
    const prevRolesRef = useRef(roles)
    const isUpdatingFromParentRef = useRef(false)
    useEffect(() => {
      const hasChanged = JSON.stringify(prevRolesRef.current) !== JSON.stringify(roles)
      if (hasChanged && roles && Array.isArray(roles) && !isAddingNew && !editingRowKey) {
        isUpdatingFromParentRef.current = true
        const transformed = roles.map((role) => {
          const roleId = role[CompanyTeamEntryFields.contact_accounts__id] || `role-${crypto.randomUUID()}`
          const companyId = role[CompanyTeamEntryFields.contact_accounts__account_id] || ''
          const companyName = role[CompanyTeamEntryFields.prog__account__name] || ''
          const categoryId = role[CompanyTeamEntryFields.contact_accounts__relationship_category_id] || ''
          const roleTypeId = role[CompanyTeamEntryFields.relationship_types__id] || ''
          const title = role[CompanyTeamEntryFields.contact_accounts__title] || ''
          const isPrimary = role[CompanyTeamEntryFields.contact_accounts__is_primary] === true

          return {
            key: roleId,
            companyId,
            companyName,
            categoryId,
            roleTypeId,
            title,
            isPrimary,
            originalRole: role
          }
        })
        setEditableRoles(transformed)
        prevRolesRef.current = roles

        setTimeout(() => {
          isUpdatingFromParentRef.current = false
        }, 0)
      } else if (hasChanged) {
        prevRolesRef.current = roles
      }
    }, [roles, isAddingNew, editingRowKey])

    // Update company names when companies are loaded
    const editableRolesRef = useRef(editableRoles)
    useEffect(() => {
      editableRolesRef.current = editableRoles
    }, [editableRoles])

    // Notify parent of changes (similar to IntroductionsTable pattern)
    const prevEditableRolesRef = useRef(editableRoles)
    const onChangeRef = useRef(onChange)

    // Keep onChange ref up to date
    useEffect(() => {
      onChangeRef.current = onChange
    }, [onChange])

    useEffect(() => {
      // Only call onChange if editableRoles actually changed (deep comparison) and we're not updating from parent
      const hasChanged = JSON.stringify(prevEditableRolesRef.current) !== JSON.stringify(editableRoles)
      if (hasChanged && onChangeRef.current && !isUpdatingFromParentRef.current) {
        prevEditableRolesRef.current = editableRoles
        // Transform editableRoles back to CompanyTeamEntry format for onChange callback
        const transformedRoles = editableRoles.map((role) => {
          const roleKey = typeof role.key === 'string' ? role.key : String(role.key)
          const roleEntry: any = {
            [CompanyTeamEntryFields.contacts__id]: contactId || null,
            [CompanyTeamEntryFields.contact_accounts__id]: roleKey.startsWith('new-') ? null : roleKey,
            [CompanyTeamEntryFields.contact_accounts__account_id]: role.companyId || null,
            [CompanyTeamEntryFields.contact_accounts__relationship_category_id]: role.categoryId || null,
            [CompanyTeamEntryFields.contact_accounts__current]: mode === 'current'
          }

          // Add optional fields if they exist
          if (role.roleTypeId) {
            roleEntry[CompanyTeamEntryFields.relationship_types__id] = role.roleTypeId
          }
          if (role.title !== undefined) {
            roleEntry[CompanyTeamEntryFields.contact_accounts__title] = role.title || null
          }
          if (mode === 'current' && role.isPrimary === true) {
            roleEntry[CompanyTeamEntryFields.contact_accounts__is_primary] = true
          }

          // Preserve company name if available (for display when restored)
          if (role.companyName) {
            roleEntry[CompanyTeamEntryFields.prog__account__name] = role.companyName
          }

          // Preserve original role data if it exists
          if (role.originalRole) {
            return { ...role.originalRole, ...roleEntry }
          }

          return roleEntry
        })
        onChangeRef.current(transformedRoles)
      } else if (hasChanged) {
        // Still update the ref even if we don't call onChange
        prevEditableRolesRef.current = editableRoles
      }
    }, [editableRoles, contactId, mode])

    // Get relationship type options - only include Employee and Advisor types
    const roleTypeOptions = useMemo(() => {
      if (relationshipTypes && relationshipTypes.length > 0) {
        return relationshipTypes
          .filter((rt: any) => {
            const typeId = rt[RelationshipTypesModelFields.id]
            return typeId === RELATIONSHIP_TYPE_EMPLOYEE || typeId === RELATIONSHIP_TYPE_ADVISOR
          })
          .map((rt: any) => ({
            value: rt[RelationshipTypesModelFields.id],
            label: rt[RelationshipTypesModelFields.type] || '',
            id: rt[RelationshipTypesModelFields.id]
          }))
      }
      return []
    }, [relationshipTypes])

    // Get relationship category options (all categories). Sourced solely from
    // the globalConfigs relationship_categories slice (cfg.contact_relationship_titles);
    // the old 'Contact Relationship Category' lookup fallback was dead — that
    // lookup group never existed in any environment.
    const allCategoryOptions = useMemo(() => {
      return (relationshipCategories || []).map(
        (cat: any): CategoryOption => ({
          value: cat[RelationshipCategoriesModelFields.id],
          label: cat[RelationshipCategoriesModelFields.category] || '',
          id: cat[RelationshipCategoriesModelFields.id],
          relationship_type_id: cat[RelationshipCategoriesModelFields.relationship_type_id]
        })
      )
    }, [relationshipCategories])

    // Helper function to get filtered category options based on role type
    const getFilteredCategoryOptions = useCallback(
      (roleTypeId: string | null | undefined) => {
        if (!roleTypeId) {
          // If no role type selected, return all categories
          return allCategoryOptions
        }

        // Filter categories by relationship_type_id
        return allCategoryOptions.filter((cat: any) => cat.relationship_type_id === roleTypeId)
      },
      [allCategoryOptions]
    )

    // Validation rules
    const roleValidationRules = useMemo(
      () => [
        createCustomValidationRule(
          'Company',
          (value, fieldName, record) => {
            const companyId = record.companyId
            if (!companyId || companyId === '') {
              return 'Company is required'
            }
            return null
          },
          'companyId'
        ),
        createCustomValidationRule(
          'Category',
          (value, fieldName, record) => {
            const categoryId = record.categoryId
            if (!categoryId || categoryId === '') {
              return 'Category is required'
            }
            return null
          },
          'categoryId'
        )
      ],
      []
    )

    // Helper function to get relationship type label
    const getRelationshipTypeLabel = useCallback(
      (relationshipTypeId: string | null | undefined): string => {
        if (!relationshipTypeId) return ''
        const relationshipType = relationshipTypes.find(
          (rt: any) => rt[RelationshipTypesModelFields.id] === relationshipTypeId
        )
        return relationshipType?.[RelationshipTypesModelFields.type] || ''
      },
      [relationshipTypes]
    )

    // Helper function to get relationship category label
    const getRelationshipCategoryLabel = useCallback(
      (categoryId: string | null | undefined): string => {
        if (!categoryId) return ''
        if (getCategoryLabelById) {
          return getCategoryLabelById(categoryId)
        }
        const category = relationshipCategories.find(
          (cat: any) => cat[RelationshipCategoriesModelFields.id] === categoryId
        )
        return category?.[RelationshipCategoriesModelFields.category] || ''
      },
      [relationshipCategories, getCategoryLabelById]
    )

    // Handle add new role
    const handleAdd = useCallback(() => {
      if (readOnly) return
      const newKey = `new-${crypto.randomUUID()}`
      const newRole: EditableRole = {
        key: newKey,
        companyId: '',
        companyName: '',
        categoryId: '',
        roleTypeId: '',
        title: '',
        isPrimary: false,
        originalRole: null
      }
      setOriginalRoleData(null)
      setIsAddingNew(true)
      const updatedRoles = [newRole, ...editableRoles]
      setEditableRoles(updatedRoles)
      setEditingRowKey(newKey)
      setFirstErrorFieldKey({ rowKey: newKey, fieldKey: 'company' })
    }, [readOnly, editableRoles])

    // Handle start editing
    const startEditing = useCallback(
      (rowKey: string) => {
        if (readOnly) return
        const currentRole = editableRoles.find((role) => role.key === rowKey)
        if (currentRole) {
          setOriginalRoleData({ ...currentRole })
        } else {
          setOriginalRoleData(null)
        }
        setEditingRowKey(rowKey)
      },
      [readOnly, editableRoles]
    )

    // Handle cancel editing
    const cancelEditing = useCallback(
      (rowKey: string) => {
        if (rowKey) {
          const isNewEntry = rowKey && typeof rowKey === 'string' && rowKey.startsWith('new-')

          if (originalRoleData) {
            setEditableRoles((prev) => prev.map((role) => (role.key === rowKey ? originalRoleData : role)))
            setIsAddingNew(false)
          } else if (isNewEntry) {
            setEditableRoles((prev) => prev.filter((role) => role.key !== rowKey))
            setIsAddingNew(false)
          }
        }
        setEditingRowKey(null)
        setSavingRowKey(null)
        setOriginalRoleData(null)
      },
      [originalRoleData]
    )

    // Wrapper for FormTable's onCancel
    const handleCancel = useCallback(() => {
      if (editingRowKey) {
        cancelEditing(editingRowKey)
      }
    }, [editingRowKey, cancelEditing])

    // Handle field change
    const handleFieldChange = useCallback(
      (rowKey: string, field: string, value: any) => {
        setEditableRoles((prev) => {
          return prev.map((role) => {
            if (role.key !== rowKey) return role

            if (field === 'company') {
              const companyId = typeof value === 'string' ? value : value?.value || value?.id || ''
              const companyName = typeof value === 'object' && value ? value.label || value.name || '' : ''

              return {
                ...role,
                companyId,
                companyName
              }
            } else if (field === 'category') {
              const categoryId = typeof value === 'object' ? value.value : value
              const roleTypeIdStr = typeof role.roleTypeId === 'string' ? role.roleTypeId : null
              const filteredCategories = getFilteredCategoryOptions(roleTypeIdStr)
              const categoryOption = filteredCategories.find((opt) => opt.value === categoryId)
              const categoryLabel = categoryOption?.label || categoryOption?.name || ''
              return {
                ...role,
                categoryId,
                categoryLabel
              }
            } else if (field === 'roleType') {
              const roleTypeId = typeof value === 'object' ? value.value : value
              // When role type changes, clear category if it's no longer valid for the new role type
              const filteredCategories = getFilteredCategoryOptions(roleTypeId)
              const currentCategoryValid = filteredCategories.some((cat: any) => cat.value === role.categoryId)
              return {
                ...role,
                roleTypeId,
                categoryId: currentCategoryValid ? role.categoryId : ''
              }
            } else if (field === 'title') {
              return {
                ...role,
                title: value || ''
              }
            } else if (field === 'isPrimary') {
              return {
                ...role,
                isPrimary: value === true
              }
            }
            return role
          })
        })
      },
      [getFilteredCategoryOptions, allCategoryOptions]
    )

    // Handle save
    const handleSave = useCallback(
      (rowKey: string) => {
        const role = editableRoles.find((r) => r.key === rowKey)
        if (!role) {
          cancelEditing(rowKey)
          return false
        }

        // Clear previous validation errors
        const errorKey = `${rowKey}-errors`
        setValidationErrors((prev) => ({
          ...prev,
          [errorKey]: {}
        }))
        setFirstErrorFieldKey(null)

        // Validate the record
        const validationResult = validateAndShowErrors(role, roleValidationRules)

        // If there are validation errors, highlight fields and focus first error
        if (!validationResult.isValid) {
          const mappedErrors: Record<string, string> = {}
          if (validationResult.errors.companyId) {
            mappedErrors.company = validationResult.errors.companyId
          }
          if (validationResult.errors.categoryId) {
            mappedErrors.category = validationResult.errors.categoryId
          }

          setValidationErrors((prev) => ({
            ...prev,
            [errorKey]: mappedErrors
          }))

          const fieldPriority = ['company', 'category']
          const firstErrorField = fieldPriority.find((field) => mappedErrors[field])

          if (firstErrorField) {
            setFirstErrorFieldKey({ rowKey: rowKey, fieldKey: firstErrorField })
          }

          return false
        }

        // Check for duplicates before saving
        const otherRoles = editableRolesRef.current.filter((r) => r.key !== rowKey)
        if (isDuplicateRole(role, otherRoles, null)) {
          toast.warning('A role with this Company and Category already exists', TOAST_CONFIG)
          return false
        }

        // All validations passed - save
        setSavingRowKey(rowKey)
        setEditingRowKey(null)
        setIsAddingNew(false)
        setFirstErrorFieldKey(null)
        setOriginalRoleData(null)

        // Call onSave callback if provided
        if (onSave) {
          onSave(role, editableRoles)
        }

        setSavingRowKey(null)
        return true
      },
      [editableRoles, cancelEditing, roleValidationRules, onSave]
    )

    // Handle remove (for FormTable actions column - only handles new unsaved entries)
    // Existing entries are deleted via ellipses menu, not this handler
    const handleRemove = useCallback(
      (rowKey: string) => {
        if (readOnly) return

        const keyStr = typeof rowKey === 'string' ? rowKey : String(rowKey)

        // Handle new entries (starting with 'new-') or when there's no onDelete handler
        // In DrawerNew, there's no onDelete, so all entries should be removable
        if (keyStr.startsWith('new-') || !onDelete) {
          setEditableRoles((prev) => {
            const filtered = prev.filter((role) => {
              const roleKey = typeof role.key === 'string' ? role.key : String(role.key)
              return roleKey !== keyStr
            })
            return filtered
          })
          if (editingRowKey === keyStr) {
            setEditingRowKey(null)
            setIsAddingNew(false)
          }
          // The onChange will be triggered by the useEffect watching editableRoles
        }
        // For existing entries with onDelete handler, do nothing - delete is handled by ellipses menu
      },
      [readOnly, editingRowKey, onDelete]
    )

    // Auto-focus first error field when validation fails
    useEffect(() => {
      if (firstErrorFieldKey && editingRowKey === firstErrorFieldKey.rowKey) {
        const timeoutId = setTimeout(() => {
          const fieldKey = firstErrorFieldKey.fieldKey
          const tableRow = document.querySelector(`tr[data-row-key="${firstErrorFieldKey.rowKey}"]`)
          if (tableRow) {
            const cells = Array.from(tableRow.querySelectorAll('td'))
            const fieldToColumnMap: Record<string, number> = {
              company: 0,
              category: 1,
              roleType: 0,
              title: 3
            }

            const columnIndex = fieldToColumnMap[fieldKey]
            if (columnIndex !== undefined) {
              const dataIndexCells = cells.filter((cell) => {
                return (
                  cell.querySelector('input, textarea, .ant-select-selector, .ant-select') &&
                  !cell.querySelector('button[class*="ant-btn"]')
                )
              })

              if (dataIndexCells[columnIndex]) {
                const targetCell = dataIndexCells[columnIndex]
                const input = targetCell.querySelector<HTMLElement>(
                  'input, textarea, .ant-select-selector, .ant-select'
                )
                if (input) {
                  if (input.classList.contains('ant-select')) {
                    const selector = input.querySelector<HTMLElement>('.ant-select-selector')
                    if (selector) {
                      selector.focus()
                      setTimeout(() => selector.click(), 50)
                    }
                  } else {
                    input.focus()
                  }
                }
              }
            }
          }
        }, 200)

        return () => clearTimeout(timeoutId)
      }
      return undefined
    }, [firstErrorFieldKey, editingRowKey])

    // Handle move to prior (for current roles table) - keep existing functionality
    const handleMoveToPrior = useCallback(
      async (record: CompanyTeamEntry) => {
        if (onMoveToPrior) {
          await onMoveToPrior(record)
        }
      },
      [onMoveToPrior]
    )

    // Handle move to current (for prior roles table) - keep existing functionality
    const handleMoveToCurrent = useCallback(
      async (record: CompanyTeamEntry) => {
        if (onMoveToCurrent) {
          await onMoveToCurrent(record)
        }
      },
      [onMoveToCurrent]
    )

    // Handle set primary (for current roles table) - keep existing functionality
    const handleSetPrimary = useCallback(
      async (record: CompanyTeamEntry) => {
        if (onSetPrimary) {
          await onSetPrimary(record)
        }
      },
      [onSetPrimary]
    )

    // Handle delete - keep existing functionality (called from ellipses menu)
    const handleDelete = useCallback(
      async (record: CompanyTeamEntry | any) => {
        // Get the key from the record (could be in editableRoles format or CompanyTeamEntry format)
        const recordKey = record?.key || record?.[CompanyTeamEntryFields.contact_accounts__id]
        const keyStr = recordKey ? (typeof recordKey === 'string' ? recordKey : String(recordKey)) : null

        // Check if it's a new entry (starts with 'new-') or has no ID (null/undefined)
        const isNewEntry = keyStr && (keyStr.startsWith('new-') || keyStr === 'null' || keyStr === 'undefined')

        // Also check if there's no onDelete handler - in that case, treat all as local entries
        if (isNewEntry || !onDelete) {
          // For new entries or when no onDelete handler, remove from local state
          if (keyStr) {
            setEditableRoles((prev) => {
              const filtered = prev.filter((role) => {
                const roleKey = typeof role.key === 'string' ? role.key : String(role.key)
                return roleKey !== keyStr
              })
              return filtered
            })
            if (editingRowKey === keyStr) {
              setEditingRowKey(null)
              setIsAddingNew(false)
            }
          }
          return
        }

        // For existing entries with onDelete handler, call onDelete with originalRole (CompanyTeamEntry format)
        const originalRole = record.originalRole || record
        await onDelete(originalRole)
      },
      [onDelete, editingRowKey]
    )

    // Custom actions column component (ellipses menu)
    const renderActionsColumn = useCallback(
      (_: any, record: any) => {
        // Don't show ellipses menu when editing (FormTable actions column handles save/cancel)
        if (editingRowKey === record.key) {
          return null
        }

        const originalRole = record.originalRole || record
        const isPrimary = originalRole[CompanyTeamEntryFields.contact_accounts__is_primary] === true
        const menuItems: any[] = []

        // For current roles table
        if (mode === 'current') {
          if (onMoveToPrior) {
            menuItems.push({
              key: 'prior',
              label: (
                <Popconfirm
                  title='Move to Prior Roles'
                  description='Are you sure you want to move this role to prior roles?'
                  okText='Yes, Move'
                  cancelText='Cancel'
                  okType='default'
                  placement='topRight'
                  onConfirm={() => handleMoveToPrior(originalRole)}
                >
                  <span className={TEXT_CLASSES.BLUE}>Prior Role</span>
                </Popconfirm>
              )
            })
          }

          if (onSetPrimary && !isPrimary) {
            menuItems.push({
              key: 'primary',
              label: (
                <Popconfirm
                  title='Set as Primary'
                  description='Are you sure you want to set this as the primary role?'
                  okText='Yes, Set Primary'
                  cancelText='Cancel'
                  okType='default'
                  placement='topRight'
                  onConfirm={() => handleSetPrimary(originalRole)}
                >
                  <span className={TEXT_CLASSES.BLUE}>Set as Primary</span>
                </Popconfirm>
              )
            })
          }

          // Always show delete option (handles both new and existing entries)
          menuItems.push({
            key: 'delete',
            label: (
              <Popconfirm
                title='Delete Role'
                description='Are you sure you want to delete this role?'
                okText='Yes, Delete'
                cancelText='Cancel'
                okType='danger'
                placement='topRight'
                onConfirm={() => handleDelete(record)}
              >
                <span className={TEXT_CLASSES.ERROR}>Delete</span>
              </Popconfirm>
            )
          })
        } else {
          // For prior roles table
          if (onMoveToCurrent) {
            menuItems.push({
              key: 'current',
              label: (
                <Popconfirm
                  title='Move to Current Roles'
                  description='Are you sure you want to move this role to current roles?'
                  okText='Yes, Move'
                  cancelText='Cancel'
                  okType='default'
                  placement='topRight'
                  onConfirm={() => handleMoveToCurrent(originalRole)}
                >
                  <span className={TEXT_CLASSES.BLUE}>Current Role</span>
                </Popconfirm>
              )
            })
          }

          // Always show delete option (handles both new and existing entries)
          menuItems.push({
            key: 'delete',
            label: (
              <Popconfirm
                title='Delete Role'
                description='Are you sure you want to delete this role?'
                okText='Yes, Delete'
                cancelText='Cancel'
                okType='danger'
                placement='topRight'
                onConfirm={() => handleDelete(record)}
              >
                <span className={TEXT_CLASSES.ERROR}>Delete</span>
              </Popconfirm>
            )
          })
        }

        if (menuItems.length === 0) {
          return null
        }

        return (
          <div className='flex justify-center'>
            <Dropdown menu={{ items: menuItems }} placement='bottomRight' trigger={['click']}>
              <Button
                type='text'
                size={UI.BUTTON_SIZE}
                className={BUTTON_CLASSES.ELLIPSES_BUTTON}
                icon={<FontAwesomeIcon icon={faEllipsisV} className={TEXT_CLASSES.SMALL} />}
              />
            </Dropdown>
          </div>
        )
      },
      [
        mode,
        editingRowKey,
        onMoveToPrior,
        onMoveToCurrent,
        onSetPrimary,
        onDelete,
        handleMoveToPrior,
        handleMoveToCurrent,
        handleSetPrimary,
        handleDelete
      ]
    )

    // Table columns with inline editing support
    const columns = useMemo(() => {
      const cols: any[] = [
        {
          ...getColumnConfig('roleType', { label: 'Role', required: false }, 150, 150),
          render: (_: any, record: any) => {
            const isEditing = editingRowKey === record.key
            const roleTypeId = record.roleTypeId
            const firstErrorField =
              firstErrorFieldKey && firstErrorFieldKey.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
            const errorKey = `${record.key}-errors`

            const roleTypeFieldConfig: SelectFieldConfig = {
              key: 'roleType',
              label: 'Role',
              type: FieldTypeEnum.SELECT,
              value: roleTypeId || '',
              editable: isEditing,
              required: false,
              options: roleTypeOptions,
              placeholder: 'Select role type...',
              allowClear: true,
              autoFocus: firstErrorField === 'roleType'
            }

            if (!isEditing) {
              return (
                <div
                  onClick={readOnly ? undefined : () => startEditing(record.key)}
                  className={readOnly ? '' : 'cursor-pointer'}
                >
                  <InputManager
                    fields={[roleTypeFieldConfig]}
                    inputMode='read'
                    labelPosition='none'
                    onChange={(key, value) => handleFieldChange(record.key, key, value)}
                    validationErrors={validationErrors[errorKey]}
                    textClassName='text-sm'
                  />
                </div>
              )
            }

            return (
              <InputManager
                fields={[roleTypeFieldConfig]}
                inputMode='edit'
                labelPosition='none'
                onChange={(key, value) => handleFieldChange(record.key, key, value)}
                validationErrors={validationErrors[errorKey]}
                textClassName='text-sm'
              />
            )
          }
        },
        {
          ...getColumnConfig('company', { label: 'Company', required: true }, 200, 200),
          render: (_: any, record: any) => {
            const isEditing = editingRowKey === record.key
            const companyId = record.companyId
            const firstErrorField =
              firstErrorFieldKey && firstErrorFieldKey.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
            const errorKey = `${record.key}-errors`

            const companyValue =
              companyId && record.companyName
                ? { value: companyId, label: record.companyName, name: record.companyName }
                : companyId || ''

            const companyFieldConfig: CompanyFieldConfig = {
              key: 'company',
              label: 'Company',
              type: FieldTypeEnum.COMPANY,
              value: companyValue,
              editable: isEditing,
              required: true,
              placeholder: 'Search companies...',
              autoFocus: firstErrorField === 'company',
              hyperlinked: true,
              hyperlinkModule: 'companies',
              getRecordId: (value: any) => value?.value || value?.id || null
            }

            if (!isEditing) {
              return (
                <div
                  onClick={readOnly ? undefined : () => startEditing(record.key)}
                  className={readOnly ? '' : 'cursor-pointer'}
                >
                  <InputManager
                    fields={[companyFieldConfig]}
                    inputMode='read'
                    labelPosition='none'
                    onChange={(key, value) => handleFieldChange(record.key, key, value)}
                    validationErrors={validationErrors[errorKey]}
                    textClassName='text-sm'
                  />
                </div>
              )
            }

            return (
              <InputManager
                fields={[companyFieldConfig]}
                inputMode='edit'
                labelPosition='none'
                onChange={(key, value) => handleFieldChange(record.key, key, value)}
                validationErrors={validationErrors[errorKey]}
                textClassName='text-sm'
              />
            )
          }
        },
        {
          ...getColumnConfig('category', { label: 'Category', required: true }, 150, 150),
          render: (_: any, record: any) => {
            const isEditing = editingRowKey === record.key
            const categoryId = record.categoryId
            const firstErrorField =
              firstErrorFieldKey && firstErrorFieldKey.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
            const errorKey = `${record.key}-errors`

            // Filter category options based on selected role type
            const filteredCategoryOptions = getFilteredCategoryOptions(record.roleTypeId)

            const categoryFieldConfig: SelectFieldConfig = {
              key: 'category',
              label: 'Category',
              type: FieldTypeEnum.SELECT,
              value: categoryId || '',
              editable: isEditing,
              required: true,
              options: filteredCategoryOptions,
              placeholder: 'Select category...',
              allowClear: false,
              autoFocus: firstErrorField === 'category'
            }

            if (!isEditing) {
              return (
                <div
                  onClick={readOnly ? undefined : () => startEditing(record.key)}
                  className={readOnly ? '' : 'cursor-pointer'}
                >
                  <InputManager
                    key={`category-${record.key}-${record.roleTypeId || 'none'}`}
                    fields={[categoryFieldConfig]}
                    inputMode='read'
                    labelPosition='none'
                    onChange={(key, value) => handleFieldChange(record.key, key, value)}
                    validationErrors={validationErrors[errorKey]}
                    textClassName='text-sm'
                  />
                </div>
              )
            }

            return (
              <InputManager
                key={`category-${record.key}-${record.roleTypeId || 'none'}`}
                fields={[categoryFieldConfig]}
                inputMode='edit'
                labelPosition='none'
                onChange={(key, value) => handleFieldChange(record.key, key, value)}
                validationErrors={validationErrors[errorKey]}
                textClassName='text-sm'
              />
            )
          }
        },
        {
          ...getColumnConfig('title', { label: 'Title', required: false }, 180, 180),
          render: (_: any, record: any) => {
            const isEditing = editingRowKey === record.key
            const title = record.title || ''
            const errorKey = `${record.key}-errors`

            const titleFieldConfig: TextFieldConfig = {
              key: 'title',
              label: 'Title',
              type: FieldTypeEnum.TEXT,
              value: title,
              editable: isEditing,
              required: false,
              placeholder: 'Enter title...'
            }

            if (!isEditing) {
              return (
                <div
                  onClick={readOnly ? undefined : () => startEditing(record.key)}
                  className={readOnly ? '' : 'cursor-pointer'}
                >
                  <InputManager
                    fields={[titleFieldConfig]}
                    inputMode='read'
                    labelPosition='none'
                    onChange={(key, value) => handleFieldChange(record.key, key, value)}
                    validationErrors={validationErrors[errorKey]}
                    textClassName='text-sm'
                  />
                </div>
              )
            }

            return (
              <InputManager
                fields={[titleFieldConfig]}
                inputMode='edit'
                labelPosition='none'
                onChange={(key, value) => handleFieldChange(record.key, key, value)}
                validationErrors={validationErrors[errorKey]}
                textClassName='text-sm'
              />
            )
          }
        }
      ]

      // Add Primary? column only for current roles
      if (mode === 'current') {
        cols.push({
          title: 'Primary?',
          key: 'primary',
          width: 100,
          render: (_: any, record: any) => {
            const isEditing = editingRowKey === record.key
            const originalRole = record.originalRole || record
            const isPrimary =
              record.isPrimary !== undefined
                ? record.isPrimary
                : originalRole[CompanyTeamEntryFields.contact_accounts__is_primary] === true
            const errorKey = `${record.key}-errors`

            // Always allow editing when in edit mode
            if (isEditing) {
              const isPrimaryFieldConfig: CheckboxFieldConfig = {
                key: 'isPrimary',
                label: '',
                type: FieldTypeEnum.CHECKBOX,
                value: isPrimary,
                editable: true,
                checkboxLabel: ''
              }

              return (
                <InputManager
                  fields={[isPrimaryFieldConfig]}
                  inputMode='edit'
                  labelPosition='none'
                  onChange={(key, value) => handleFieldChange(record.key, key, value)}
                  validationErrors={validationErrors[errorKey]}
                  textClassName='text-sm'
                />
              )
            }

            // Read-only display - always allow editing
            return (
              <div
                onClick={readOnly ? undefined : () => startEditing(record.key)}
                className={readOnly ? 'text-sm' : 'text-sm cursor-pointer'}
              >
                {isPrimary ? 'Yes' : 'No'}
              </div>
            )
          }
        })
      }

      return cols
    }, [
      mode,
      editingRowKey,
      readOnly,
      firstErrorFieldKey,
      validationErrors,
      roleTypeOptions,
      getFilteredCategoryOptions,
      handleFieldChange,
      startEditing
    ])

    // Expose pending check function to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        hasPendingRoles: () => {
          return isAddingNew || editableRoles.some((role) => !role.companyId || !role.categoryId)
        }
      }),
      [isAddingNew, editableRoles]
    )

    const title = mode === 'current' ? 'Employee and other roles' : 'Prior roles'

    return (
      <FormTable
        title={title}
        dataSource={editableRoles}
        columns={columns}
        includeActions={!readOnly}
        mode='existing'
        useDeleteConfirm={false}
        isAddingNew={isAddingNew}
        onAdd={readOnly ? undefined : handleAdd}
        addButtonText=''
        addButtonIcon={faPlus}
        editingRowKey={editingRowKey}
        savingRowKey={savingRowKey}
        onSave={handleSave}
        onCancel={handleCancel}
        onRemove={readOnly ? undefined : handleRemove}
        hideDeleteWhenNotEditing={true}
        customActionsRenderer={renderActionsColumn}
      />
    )
  }
)

ContactRolesTable.displayName = 'ContactRolesTable'
